import React, { useEffect, useState, useCallback } from "react";
import { Bell, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export function NotificationBell() {
  const [invites, setInvites] = useState([]);
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/invitations/mine").then((r) => setInvites(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setNotes(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const unread = invites.length + notes.filter((n) => !n.read).length;

  const respond = async (id, accept) => {
    try {
      await api.post(`/invitations/${id}/${accept ? "accept" : "decline"}`);
      toast.success(accept ? "Invitation accepted." : "Invitation declined.");
      load();
    } catch (e) { toast.error(e.message); }
  };
  const markRead = async (n) => { if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); load(); } catch (e) { console.error("Failed to mark notification read", e); } } };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative w-9 h-9 rounded-full bg-nb-surface flex items-center justify-center text-nb-ink2 hover:text-nb-ink transition-colors" style={{ border: "1px solid var(--border-control)" }} data-testid="notifications-bell" title="Notifications">
          <Bell size={16} strokeWidth={1.5} />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center" style={{ background: "var(--brand-kawaii)", color: "var(--brand-navy)" }} data-testid="notif-count">{unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0 bg-nb-surface" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border-subtle)" }}>
          <span className="text-[14px] font-semibold text-nb-ink">Notifications</span>
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {invites.map((i) => (
            <div key={i.id} className="px-4 py-3 border-b" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-selected)" }} data-testid={`invite-${i.id}`}>
              <p className="text-[13px] text-nb-ink"><strong>{i.invited_by}</strong> invited you to join <strong>{i.team}</strong>.</p>
              <div className="flex gap-2 mt-2">
                <button onClick={() => respond(i.id, true)} data-testid={`invite-accept-${i.id}`} className="inline-flex items-center gap-1 text-[12.5px] font-semibold rounded-md px-2.5 py-1" style={{ background: "var(--brand-navy)", color: "var(--brand-silky)" }}><Check size={13} /> Accept</button>
                <button onClick={() => respond(i.id, false)} data-testid={`invite-decline-${i.id}`} className="inline-flex items-center gap-1 text-[12.5px] rounded-md px-2.5 py-1 text-nb-ink2" style={{ border: "1px solid var(--border-control)" }}><X size={13} /> Decline</button>
              </div>
            </div>
          ))}
          {notes.map((n) => (
            <button key={n.id} onClick={() => markRead(n)} className="w-full text-left px-4 py-3 border-b hover:bg-[var(--bg-selected)] transition-colors" style={{ borderColor: "var(--border-subtle)" }}>
              <p className="text-[13px] text-nb-ink flex items-start gap-2">
                {!n.read && <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: "var(--brand-kawaii)" }} />}
                <span className={n.read ? "text-nb-ink2" : ""}>{n.text}</span>
              </p>
            </button>
          ))}
          {invites.length === 0 && notes.length === 0 && (
            <p className="px-4 py-6 text-[13px] text-nb-ink2 text-center">You are all caught up.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
